import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Search, Users } from 'lucide-react';
import { Card } from '../../../components/Card';
import { Input } from '../../../components/Input';
import { StatCard } from '../../../components/StatCard';
import { EmptyState } from '../../../components/EmptyState';
import { useAttendanceStore, type WorkforceReportRow } from '../../../store/attendanceStore';
import type { ExportColumn } from '../../../lib/export';
import { fmtDate, fmtMinutes } from './attendanceUi';
import { ExportButton } from './ExportButton';
import { EmployeeReportView } from './EmployeeReportView';
import { ReportRangePicker, rangePreset, type DateRange } from './ReportRangePicker';

const hours = (minutes: number) => Math.round((minutes / 60) * 100) / 100;

const columns: ExportColumn<WorkforceReportRow>[] = [
    { header: 'Employee', value: (r) => r.employeeName },
    { header: 'Code', value: (r) => r.employeeCode },
    { header: 'Department', value: (r) => r.department },
    { header: 'Designation', value: (r) => r.designation },
    { header: 'Present days', value: (r) => r.presentDays },
    { header: 'Half days', value: (r) => r.halfDays },
    { header: 'Absent days', value: (r) => r.absentDays },
    { header: 'Leave days', value: (r) => r.leaveDays },
    { header: 'Late days', value: (r) => r.lateDays },
    { header: 'WFH days', value: (r) => r.wfhDays },
    { header: 'Missing check-outs', value: (r) => r.missingCheckoutDays },
    { header: 'Worked hours', value: (r) => hours(r.workedMinutes) },
    { header: 'Avg hours/day', value: (r) => hours(r.avgMinutesPerDay) },
    { header: 'Avg hours/week', value: (r) => hours(r.avgMinutesPerWeek) },
    { header: 'Adjustments', value: (r) => r.adjustments },
];

/**
 * All-employees attendance performance over a period (managers see their team,
 * HR everyone): one aggregated row per person, with export, and a click-through
 * into any employee's full day-by-day report.
 */
export function WorkforceReport() {
    const { fetchWorkforceReport } = useAttendanceStore();
    const [range, setRange] = useState<DateRange>(() => rangePreset('thisMonth'));
    const [rows, setRows] = useState<WorkforceReportRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchWorkforceReport(range.from, range.to)
            .then((r) => { if (!cancelled) setRows(r.employees); })
            .catch((e) => { if (!cancelled) toast.error(e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [range.from, range.to, fetchWorkforceReport]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? rows.filter((r) => `${r.employeeName} ${r.employeeCode} ${r.department}`.toLowerCase().includes(q)) : rows;
    }, [rows, search]);

    const totals = useMemo(() => {
        const worked = rows.reduce((n, r) => n + r.workedMinutes, 0);
        return {
            employees: rows.length,
            avgPerEmployee: rows.length ? Math.round(worked / rows.length) : 0,
            late: rows.reduce((n, r) => n + r.lateDays, 0),
            absent: rows.reduce((n, r) => n + r.absentDays, 0),
        };
    }, [rows]);

    if (selected) {
        return <EmployeeReportView range={range} employeeId={selected} showName onBack={() => setSelected(null)} />;
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <ReportRangePicker value={range} onChange={setRange} />
                <div className="flex items-center gap-2">
                    <div className="w-full sm:w-64">
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, code or department"
                            leftIcon={<Search className="size-4 text-muted-foreground" />} />
                    </div>
                    <ExportButton rows={filtered} columns={columns} baseName={`attendance-workforce_${range.from}_to_${range.to}`} disabled={loading} />
                </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Employees" value={totals.employees} icon={<Users className="size-4" />} />
                <StatCard label="Avg hours worked" value={fmtMinutes(totals.avgPerEmployee)} hint="per employee, this range" />
                <StatCard label="Late days" value={totals.late} hint="across all employees" />
                <StatCard label="Absent days" value={totals.absent} hint="across all employees" />
            </div>

            <Card>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                    <div>
                        <h2 className="font-display text-card-title text-foreground">Per-employee summary <span className="text-sm font-normal text-muted-foreground">{filtered.length}</span></h2>
                        <p className="text-xs text-muted-foreground">
                            {fmtDate(range.from, { day: 'numeric', month: 'short', year: 'numeric' })} – {fmtDate(range.to, { day: 'numeric', month: 'short', year: 'numeric' })} · click a row for the full report
                        </p>
                    </div>
                </div>
                {filtered.length === 0 ? (
                    <EmptyState className="m-4 border-0" title={loading ? 'Loading…' : 'No employees'} description="Try another date range or search." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-2 font-semibold">Employee</th>
                                    <th className="px-4 py-2 text-right font-semibold">Present</th>
                                    <th className="px-4 py-2 text-right font-semibold">Absent</th>
                                    <th className="px-4 py-2 text-right font-semibold">Leave</th>
                                    <th className="px-4 py-2 text-right font-semibold">Late</th>
                                    <th className="px-4 py-2 text-right font-semibold">WFH</th>
                                    <th className="px-4 py-2 text-right font-semibold">Adj.</th>
                                    <th className="px-4 py-2 text-right font-semibold">Worked</th>
                                    <th className="px-4 py-2 text-right font-semibold">Avg/day</th>
                                    <th className="px-4 py-2 text-right font-semibold">Avg/week</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filtered.map((r) => (
                                    <tr key={r.employeeId} className="cursor-pointer hover:bg-secondary/30" onClick={() => setSelected(r.employeeId)}>
                                        <td className="px-4 py-2">
                                            <p className="font-medium text-foreground">{r.employeeName || 'Unnamed'}</p>
                                            <p className="text-xs text-muted-foreground">{[r.employeeCode, r.designation || r.department].filter(Boolean).join(' · ')}</p>
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums text-foreground">{r.presentDays}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.absentDays || '—'}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.leaveDays || '—'}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.lateDays || '—'}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.wfhDays || '—'}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.adjustments || '—'}</td>
                                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-foreground">{r.workedMinutes ? fmtMinutes(r.workedMinutes) : '—'}</td>
                                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-muted-foreground">{r.avgMinutesPerDay ? fmtMinutes(r.avgMinutesPerDay) : '—'}</td>
                                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-muted-foreground">{r.avgMinutesPerWeek ? fmtMinutes(r.avgMinutesPerWeek) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
